import {Injectable, inject} from '@angular/core'
import {INFOUSER} from '@common/global-constants'
import {UVEuser} from '@models/uve-user'
import {EmbeddedClientService} from '@service/embeddingService/embedded-client.service'
import {NewOrderService} from '@service/orders/new/new-order.service'
import {AuthService} from './auth.service'

/**
 * Variante de `AuthService` para el modo embebido de UVE Street.
 *
 * @description
 * Se usa cuando la aplicación se ejecuta embebida dentro de un host (por
 * ejemplo, Connecta-UX) que ya tiene una sesión activa. En lugar de pedir
 * credenciales al usuario, obtiene la sesión desde `EmbeddedClientService`
 * (puente con el host) y la propaga a `UVEApiService`, `FetchService` y al
 * `TranslationServicex` para el idioma.
 *
 * Capa de Aplicación: sobrescribe `authenticated` y `clearAllData` de la
 * versión estándar para que el login delegue en el host embebido y la
 * limpieza de datos evite destruir el estado persistido de PouchDB (el host
 * es quien controla el ciclo de vida).
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * Se registra como override de `AuthService` sólo en la configuración del
 * bundle embebido (ver `src/app/app.config.ts` variants).
 *
 * ```typescript
 * {provide: AuthService, useClass: AuthEmbeddedService}
 * ```
 *
 * ### Best Practices
 *
 * - No invocar directamente este servicio desde features: las features
 *   consumen `AuthService` y el inyector resuelve la variante correcta.
 * - Preservar la sincronía con el host: `authenticated()` revalida el token
 *   cada vez que se invoca; no cachear su resultado.
 *
 * ### Bad Uses / Errores comunes
 *
 * - Reemplazar manualmente `AuthService` por `AuthEmbeddedService` en el
 *   bundle web: provocaría errores al intentar contactar con el host.
 * - Llamar a `clearAllData()` esperando que limpie PouchDB: en modo
 *   embebido sólo borra `INFOUSER` y el pedido en curso.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthEmbeddedService extends AuthService {
  private readonly embeddedService: EmbeddedClientService = inject(EmbeddedClientService)
  private readonly newOrderService: NewOrderService = inject(NewOrderService)
  override getUveUser = (): UVEuser => {
    return this.embeddedService.getSessionUser()
  }

  override authenticated = async (): Promise<boolean> => {
    const session = this.embeddedService.getSessionUser()
    this.uveUser = session
    await this.dbService.setItemLocalInfo(INFOUSER, session)
    const token = await this.getSessionToken()
    this.uveApiService.setSessionUser(this.uveUser, false)
    this.fetchService.setSessionUser(this.uveUser)
    this.translationServicex.setLanguage(this.uveUser.language)

    if ((this.isAuthenticated || !this.networkService.isOnline()) && !!this.uveUser) {
      return true
    }

    this.uveApiService.setSessionUser(this.uveUser, false)
    this.fetchService.setSessionUser(this.uveUser)
    if (!token) {
      return false
    }
    if (this.networkService.isOnline()) {
      this._isAuthenticated = await this.setAuthState()
      return this.isAuthenticated
    }
    await this.prepareUserAndDBSessions()
    this._isAuthenticated = true
    return true
  }

  override clearAllData = async (destroyAll = true): Promise<void> => {
    await this.dbService.deleteItemLocalInfo(INFOUSER)
    this.newOrderService.clearOrder()
  }
}
